/**
 * ĐỊNH DẠNG THỜI GIAN THEO GIỜ VIỆT NAM (GMT+7).
 *
 * ════════════════════════════════════════════════════════════════════════
 * VÌ SAO PHẢI CÓ FILE NÀY
 * ════════════════════════════════════════════════════════════════════════
 *
 * `toLocaleTimeString('vi-VN')` KHÔNG cho ra giờ Việt Nam. Nó cho ra giờ
 * theo MÚI GIỜ CỦA THIẾT BỊ, chỉ định dạng theo quy ước Việt Nam.
 *
 * Điện thoại đặt sai múi giờ (hoặc nhân viên cố tình đổi sang múi giờ khác)
 * sẽ hiển thị giờ chấm công lệch vài tiếng so với con số thật trong bảng
 * công. Trong hệ thống tính lương, đó là nguồn tranh chấp trực tiếp.
 *
 * Mọi hàm dưới đây ép `timeZone: 'Asia/Ho_Chi_Minh'`, nên kết quả giống hệt
 * nhau trên mọi thiết bị bất kể cài đặt máy.
 *
 * Quy tắc chung của hệ thống (docs/04 §4.6):
 *   · LƯU     → UTC (TIMESTAMPTZ)
 *   · HIỂN THỊ → GMT+7
 *   · work_date → tính theo GMT+7, không theo UTC
 */

export const MUI_GIO_VN = 'Asia/Ho_Chi_Minh'

/** Giờ:phút — dùng cho đồng hồ và mốc chấm công. */
export function gio(d: Date | string | number): string {
  return new Date(d).toLocaleTimeString('vi-VN', {
    timeZone: MUI_GIO_VN,
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Giờ:phút:giây — dùng cho biên nhận sau khi chấm công. */
export function gioGiay(d: Date | string | number): string {
  return new Date(d).toLocaleTimeString('vi-VN', {
    timeZone: MUI_GIO_VN,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** Thứ, ngày tháng năm đầy đủ. */
export function ngayDayDu(d: Date | string | number): string {
  return new Date(d).toLocaleDateString('vi-VN', {
    timeZone: MUI_GIO_VN,
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  })
}

/** Ngày giờ đầy đủ — dùng khi cần cả hai. */
export function ngayGio(d: Date | string | number): string {
  return new Date(d).toLocaleString('vi-VN', {
    timeZone: MUI_GIO_VN,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Ngày công theo giờ Việt Nam, dạng YYYY-MM-DD.
 *
 * Quan trọng với ca đêm: 00:30 giờ VN ngày 10 tương ứng 17:30 UTC ngày 09.
 * Lấy `toISOString().slice(0,10)` sẽ ra ngày 09 — SAI một ngày công.
 */
export function ngayCong(d: Date | string | number = new Date()): string {
  // en-CA cho ra đúng định dạng YYYY-MM-DD
  return new Date(d).toLocaleDateString('en-CA', { timeZone: MUI_GIO_VN })
}

/** Tháng hiện tại theo giờ Việt Nam, dạng YYYY-MM. */
export function thangHienTai(): string {
  return ngayCong().slice(0, 7)
}

/** Số ngày trong tháng — dùng cho lịch sử. Nhận chuỗi YYYY-MM. */
export function soNgayTrongThang(thang: string): number {
  const [nam, m] = thang.split('-').map(Number)
  return new Date(nam, m, 0).getDate()
}

/** Ngày — "11/09/2026". */
export function ngay(d: Date | string | number): string {
  return new Date(d).toLocaleDateString('vi-VN', {
    timeZone: MUI_GIO_VN, day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

/**
 * Số thứ trong tuần của một ngày YYYY-MM-DD, theo giờ Việt Nam.
 * 0 = Chủ nhật … 6 = Thứ Bảy.
 *
 * Dựng `Date` từ chuỗi `YYYY-MM-DD` TRẦN là bẫy: trình duyệt hiểu nó là
 * NỬA ĐÊM UTC, tức 07:00 giờ VN cùng ngày — may là vẫn đúng ngày. Nhưng
 * `new Date('2026-09-11')` ở máy đặt múi giờ ÂM (châu Mỹ) lại ra ngày 10.
 * Thêm `T00:00:00` để trình duyệt hiểu là giờ ĐỊA PHƯƠNG và không đổi ngày.
 */
export function thuTrongTuan(ngayISO: string): number {
  return new Date(`${ngayISO}T00:00:00`).getDay()
}

/** "T2" … "T7", "CN". */
export function thuNgan(ngayISO: string): string {
  return ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][thuTrongTuan(ngayISO)]
}

/** Số giờ thập phân → "8h 13m". API trả `work_hours` dạng số thực. */
export function gioTuSoThuc(soGio: number | null | undefined): string {
  if (soGio === null || soGio === undefined) return '—'
  const p = Math.max(0, Math.round(soGio * 60))
  const h = Math.floor(p / 60)
  return h > 0 ? `${h}h ${String(p % 60).padStart(2, '0')}m` : `${p}m`
}

/** Dịch một tháng YYYY-MM đi `buoc` tháng. `-1` = tháng trước. */
export function dichThang(thang: string, buoc: number): string {
  const [nam, m] = thang.split('-').map(Number)
  const d = new Date(nam, m - 1 + buoc, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** "Tháng 9, 2026" — nhãn tháng trong bản thiết kế. */
export function nhanThang(thang: string): string {
  const [nam, m] = thang.split('-')
  return `Tháng ${Number(m)}, ${nam}`
}

/** Ngày YYYY-MM-DD lùi `n` ngày so với `goc`, theo giờ VN. */
export function ngayLui(goc: Date | string, n: number): string {
  const d = typeof goc === 'string' ? new Date(`${goc}T00:00:00`) : new Date(goc)
  d.setDate(d.getDate() - n)
  return ngayCong(d)
}
